import Phaser from 'phaser';
import { textureKey, TIERS, Tier, crescentPhysicsVerts, polygonVerts, getTierScale } from './shapes';

type Vec = { x: number; y: number };

// Matter's compound-body helpers are namespaced under Phaser's matter bundle.
const Matter = (Phaser.Physics.Matter as unknown as { Matter: typeof MatterJS & {
  Body: { create(options: object): MatterJS.BodyType };
  Bodies: {
    rectangle(x: number, y: number, w: number, h: number, opts?: object): MatterJS.BodyType;
    circle(x: number, y: number, radius: number, opts?: object): MatterJS.BodyType;
    fromVertices(x: number, y: number, verts: Vec[], opts?: object, flagInternal?: boolean, removeCollinear?: number, minimumArea?: number): MatterJS.BodyType;
  };
  Vertices: { centre(verts: Vec[]): Vec };
} }).Matter;

// Texture footprint padding — must match `radius * 2 + 6` used when the textures
// are rendered (see shapes.ts generateStyledTexture).
const TEX_PAD = 6;

/**
 * Align the sprite to a body whose center of mass is offset from the texture
 * center. Matter positions every body at its area centroid (the COM), but each
 * texture is drawn centered on the shape's bounding-circle center. For shapes
 * whose centroid isn't the bounding center (crescent → sideways, heart → down),
 * that gap shows up as the collider drifting off the candy. Shifting the render
 * origin by the centroid offset (as a fraction of the texture size) puts the
 * drawn candy back exactly over its collider.
 */
function alignSpriteToBody(image: ShapeBody, comLocal: Vec, tier: Tier): void {
  const scale = getTierScale();
  const texSize = tier.radius * 2 + TEX_PAD;
  // comLocal is from the unscaled body; dividing by texSize*scale keeps the
  // origin fraction correct after setScale() expands the displayed sprite.
  image.setOrigin(0.5 + comLocal.x / (texSize * scale), 0.5 + comLocal.y / (texSize * scale));
  image.setScale(scale);
}

export interface ShapeBody extends Phaser.Physics.Matter.Image {
  shapeTier: number;
  shapeId: number;
  merging?: boolean;
}

let nextShapeId = 1;

/**
 * Spawns a Matter physics body for the given tier at (x, y).
 *
 * Most shapes use a circle collider sized to physicsRadiusFor(kind, radius)
 * so their visible silhouettes touch when their colliders touch. The PLUS
 * tier is special — a single circle either leaves the arms poking out of
 * other shapes (small radius) or makes the empty corners between arms
 * collide with nothing visibly there (big radius). For plus we build a
 * compound body from two crossed rectangles so the collider matches the
 * cross shape exactly.
 */
export function createShape(
  scene: Phaser.Scene,
  tierIndex: number,
  x: number,
  y: number,
  opts: { isStatic?: boolean } = {},
): ShapeBody {
  const tier: Tier = TIERS[tierIndex];
  const key = textureKey(tierIndex);

  const baseProps = {
    isStatic: opts.isStatic ?? false,
    friction: 0.25,
    frictionAir: 0.001,
    restitution: 0.15,
    density: 0.0015,
    label: `tier-${tierIndex}`,
  };

  let image: ShapeBody;

  // Every shape gets a collider that matches its on-screen silhouette so flats
  // rest on flats and edges catch edges (no more rolling circle approximations).
  // Convex polygons use their vertices directly; concave shapes (crescent, star)
  // are decomposed by the bundled poly-decomp; plus and heart use compound bodies
  // of primitives that exactly reproduce how the texture is drawn. The KING is a
  // genuine disc, so it keeps a circle collider.
  const r = tier.radius;

  const newImage = (): ShapeBody =>
    scene.matter.add.image(x, y, key, undefined, {
      shape: { type: 'circle', radius: 1 },
      ...baseProps,
    }) as ShapeBody;

  const setBody = (body: MatterJS.BodyType, comLocal: Vec): void => {
    image.setExistingBody(body, true);
    image.setPosition(x, y);
    alignSpriteToBody(image, comLocal, tier);
  };

  if (tier.kind === 'circle') {
    // Truly round — a circle collider IS the exact silhouette.
    image = scene.matter.add.image(x, y, key, undefined, {
      shape: { type: 'circle', radius: r },
      ...baseProps,
    }) as ShapeBody;
    image.setScale(getTierScale());
  } else if (tier.kind === 'plus') {
    // Two crossed rectangles — exact, symmetric (COM at center).
    const arm = r;
    const thick = r * 0.42;
    image = newImage();
    const horiz = Matter.Bodies.rectangle(0, 0, arm * 2, thick);
    const vert = Matter.Bodies.rectangle(0, 0, thick, arm * 2);
    const compound = Matter.Body.create({ parts: [horiz, vert], ...baseProps });
    setBody(compound as unknown as MatterJS.BodyType, { x: 0, y: 0 });
  } else if (tier.kind === 'heart') {
    // Two lobe circles + the V triangle — mirrors _drawProceduralMask exactly.
    const lo = r * 0.35;
    const lr = r * 0.42;
    const ly = -r * 0.2;
    const loOuter = lo + lr;
    const lmid = ly + lr * 0.3;
    const bottom = r * 0.88;
    image = newImage();
    const leftLobe = Matter.Bodies.circle(-lo, ly, lr);
    const rightLobe = Matter.Bodies.circle(lo, ly, lr);
    // fromVertices recenters the part on its own centroid, so create it AT that
    // centroid to keep it where it belongs in the compound (reaching the tip).
    const triVerts = [
      { x: -loOuter, y: lmid },
      { x: loOuter, y: lmid },
      { x: 0, y: bottom },
    ];
    const triC = Matter.Vertices.centre(triVerts);
    const vtri = Matter.Bodies.fromVertices(triC.x, triC.y, triVerts, baseProps);
    const compound = Matter.Body.create({ parts: [leftLobe, rightLobe, vtri], ...baseProps });
    const com = (compound as unknown as { position: Vec }).position;
    setBody(compound as unknown as MatterJS.BodyType, { x: com.x, y: com.y });
  } else {
    // Polygon-based: convex regular polygons, the star, and the crescent.
    const verts = tier.kind === 'crescent' ? crescentPhysicsVerts(r) : polygonVerts(tier.kind, r);
    const com = Matter.Vertices.centre(verts);
    image = newImage();
    const body = Matter.Bodies.fromVertices(x, y, verts, baseProps, false, 0.01, 5);
    image.setExistingBody(body as unknown as MatterJS.BodyType, true);
    image.setPosition(x, y);
    alignSpriteToBody(image, com, tier);
  }

  image.shapeTier = tierIndex;
  image.shapeId = nextShapeId++;
  image.setFixedRotation();
  return image;
}
